import { authService } from '../supabaseAuthService'
import type {
  SignUpData,
  SignInData,
  PasswordResetData,
  ProfileUpdateData,
  AuthResult,
  AuthError,
  OAuthConfig,
  CraftyJoyUser,
} from '../types/auth'
import type { Session } from '@supabase/supabase-js'

/**
 * Authentication API Layer
 * 
 * This module provides a clean API interface for authentication operations.
 * It acts as a wrapper around the Supabase auth service and can be easily
 * extended to work with other backends or custom API endpoints.
 */

export class AuthAPI {
  private static instance: AuthAPI

  private constructor() {}

  public static getInstance(): AuthAPI {
    if (!AuthAPI.instance) {
      AuthAPI.instance = new AuthAPI()
    }
    return AuthAPI.instance
  }

  // Authentication Endpoints

  /**
   * Sign up a new user
   * POST /api/auth/signup
   */
  async signup(userData: SignUpData): Promise<AuthResult> {
    try {
      const result = await authService.signUp(userData)
      
      // Log signup attempt for analytics
      this.logAuthEvent('signup_attempt', {
        email: userData.email,
        role: userData.role,
        success: !result.error,
      })

      return result
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Sign in an existing user
   * POST /api/auth/signin
   */
  async signin(credentials: SignInData): Promise<AuthResult> {
    try {
      const result = await authService.signIn(credentials)
      
      // Log signin attempt for analytics
      this.logAuthEvent('signin_attempt', {
        email: credentials.email,
        success: !result.error,
      })

      return result
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Sign in with OAuth provider
   * POST /api/auth/oauth
   */
  async signInWithOAuth(config: OAuthConfig): Promise<AuthResult> {
    try {
      const result = await authService.signInWithOAuth(config)
      
      this.logAuthEvent('oauth_attempt', {
        provider: config.provider,
        success: !result.error,
      })

      return result
    } catch (error) {
      return {
        user: null,
        session: null,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Sign out current user
   * POST /api/auth/signout
   */
  async signout(): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const result = await authService.signOut()
      
      this.logAuthEvent('signout', {
        success: !result.error,
      })

      return {
        success: !result.error,
        error: result.error,
      }
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Request password reset
   * POST /api/auth/reset-password
   */
  async resetPassword(data: PasswordResetData): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const result = await authService.resetPassword(data)
      
      this.logAuthEvent('password_reset_request', {
        email: data.email,
        success: !result.error,
      })

      return {
        success: !result.error,
        error: result.error,
      }
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Update user password
   * POST /api/auth/update-password
   */
  async updatePassword(newPassword: string): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const result = await authService.updatePassword(newPassword)
      
      this.logAuthEvent('password_update', {
        success: !result.error,
      })

      return {
        success: !result.error,
        error: result.error,
      }
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Verify email address
   * POST /api/auth/verify-email
   */
  async verifyEmail(token: string): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      // This would typically be handled by Supabase automatically
      // when the user clicks the email verification link
      
      this.logAuthEvent('email_verification', {
        success: true,
      })

      return {
        success: true,
        error: null,
      }
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error),
      }
    }
  }

  // Session Management Endpoints

  /**
   * Get current session
   * GET /api/auth/session
   */
  async getSession(): Promise<Session | null> {
    try {
      return await authService.getSession()
    } catch (error) {
      console.error('Error getting session:', error)
      return null
    }
  }

  /**
   * Refresh current session
   * POST /api/auth/refresh
   */
  async refreshSession(): Promise<Session | null> {
    try {
      const session = await authService.refreshSession()
      
      this.logAuthEvent('session_refresh', {
        success: !!session,
      })

      return session
    } catch (error) {
      console.error('Error refreshing session:', error)
      return null
    }
  }

  /**
   * Get current user
   * GET /api/auth/user
   */
  async getCurrentUser(): Promise<CraftyJoyUser | null> {
    try {
      return await authService.getCurrentUser()
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  }

  // Profile Management Endpoints

  /**
   * Update user profile
   * PUT /api/auth/profile
   */
  async updateProfile(userId: string, updates: ProfileUpdateData): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const result = await authService.updateProfile(userId, updates)
      
      this.logAuthEvent('profile_update', {
        userId,
        fields: Object.keys(updates),
        success: !result.error,
      })

      return {
        success: !result.error,
        error: result.error,
      }
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Get user profile
   * GET /api/auth/profile/:userId
   */
  async getUserProfile(userId: string) {
    try {
      return await authService.getUserProfile(userId)
    } catch (error) {
      console.error('Error getting user profile:', error)
      return null
    }
  }

  // Validation Endpoints

  /**
   * Check if username is available
   * GET /api/auth/check-username?username=value
   */
  async checkUsernameAvailability(username: string): Promise<{ available: boolean; error: AuthError | null }> {
    try {
      // This would typically be a database query
      // For now, we'll simulate the check
      const isValid = this.validateUsername(username)
      
      if (!isValid.valid) {
        return {
          available: false,
          error: { message: isValid.reason || 'Invalid username' },
        }
      }

      // In a real implementation, you'd check the database
      // For now, return true (available)
      return {
        available: true,
        error: null,
      }
    } catch (error) {
      return {
        available: false,
        error: this.handleError(error),
      }
    }
  }

  /**
   * Check if email is available
   * GET /api/auth/check-email?email=value
   */
  async checkEmailAvailability(email: string): Promise<{ available: boolean; error: AuthError | null }> {
    try {
      const isValid = this.validateEmail(email)
      
      if (!isValid) {
        return {
          available: false,
          error: { message: 'Invalid email format' },
        }
      }

      // In a real implementation, you'd check the database
      // For now, return true (available)
      return {
        available: true,
        error: null,
      }
    } catch (error) {
      return {
        available: false,
        error: this.handleError(error),
      }
    }
  }

  // Utility Methods

  private validateUsername(username: string): { valid: boolean; reason?: string } {
    if (!username || username.length < 3) {
      return { valid: false, reason: 'Username must be at least 3 characters long' }
    }

    if (username.length > 20) {
      return { valid: false, reason: 'Username must be 20 characters or less' }
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      return { valid: false, reason: 'Username can only contain letters, numbers, underscores, and hyphens' }
    }

    const reservedUsernames = [
      'admin', 'administrator', 'root', 'system', 'support',
      'help', 'api', 'www', 'mail', 'ftp', 'craftyjoy',
      'moderator', 'bot', 'null', 'undefined', 'test'
    ]

    if (reservedUsernames.includes(username.toLowerCase())) {
      return { valid: false, reason: 'This username is reserved' }
    }

    return { valid: true }
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  private handleError(error: any): AuthError {
    console.error('Auth API error:', error)
    
    return {
      message: error.message || 'An unexpected error occurred',
      code: error.code,
      status: error.status,
    }
  }

  private logAuthEvent(event: string, data: any): void {
    // In a real implementation, this would send to analytics
    if (import.meta.env.VITE_APP_ENVIRONMENT === 'dev') {
      console.log(`Auth Event: ${event}`, data)
    }
  }

  // Demo Mode Helpers

  isDemoMode(): boolean {
    return authService.isDemoMode()
  }

  getConfigStatus() {
    return authService.getConfigStatus()
  }
}

// Create and export singleton instance
export const authAPI = AuthAPI.getInstance()

// Export individual functions for easier imports
export const {
  signup,
  signin,
  signInWithOAuth,
  signout,
  resetPassword,
  updatePassword,
  verifyEmail,
  getSession,
  refreshSession,
  getCurrentUser,
  updateProfile,
  getUserProfile,
  checkUsernameAvailability,
  checkEmailAvailability,
  isDemoMode,
  getConfigStatus,
} = authAPI